import AddIcon from '@mui/icons-material/Add'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useStaff } from '@/api/hooks'
import { STAFF_ROLES as ROLES, StaffDialog } from '@/pages/StaffDialog'
import type { StaffMember } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatMobile,
  formatMoney,
  humanise,
  PageHeader,
} from '@/components/common'

export function StaffPage() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const [role, setRole] = useState('')
  const [employmentType, setEmploymentType] = useState('')
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<StaffMember | null>(null)
  const [adding, setAdding] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)

  // The branch comes from the toolbar and applies to every screen at once.
  const { data, isLoading, error } = useStaff({
    role: role || undefined,
    employmentType: employmentType || undefined,
    q: q || undefined,
    includeInactive,
  })

  return (
    <>
      <PageHeader
        title="Staff"
        subtitle="Employees, contract workers and vendor-supplied manpower"
        actions={
          canWrite('manpower') ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
              Add staff
            </Button>
          ) : null
        }
      />
      <ErrorBanner error={error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search name or code"
              value={q}
              onChange={(event) => setQ(event.target.value)}
              sx={{ minWidth: 240 }}
            />
            <TextField
              select
              label="Role"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All roles</MenuItem>
              {ROLES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Employment"
              value={employmentType}
              onChange={(event) => setEmploymentType(event.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All</MenuItem>
              {['EMPLOYEE', 'CONTRACT', 'VENDOR_SUPPLIED'].map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeInactive}
                  onChange={(event) => setIncludeInactive(event.target.checked)}
                />
              }
              label="Show inactive"
            />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        {isLoading && <LinearProgress />}
        {data && data.length === 0 ? (
          <EmptyState message="No staff match these filters." />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Employment</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell align="right">Per shift</TableCell>
                <TableCell align="right">Per hour</TableCell>
                <TableCell align="right">Rating</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((staff) => (
                <TableRow
                  key={staff.id}
                  hover
                  sx={canWrite('manpower') ? { cursor: 'pointer' } : undefined}
                  onClick={() => canWrite('manpower') && setEditing(staff)}
                >
                  <TableCell>{staff.staffCode}</TableCell>
                  <TableCell>{staff.fullName}</TableCell>
                  <TableCell>{humanise(staff.primaryRole)}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={humanise(staff.employmentType)} />
                  </TableCell>
                  <TableCell>{formatMobile(staff.mobile)}</TableCell>
                  <TableCell align="right">{formatMoney(staff.costPerShift)}</TableCell>
                  <TableCell align="right">{formatMoney(staff.costPerHour)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {staff.performanceRating ? staff.performanceRating.toFixed(1) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {staff.active ? (
                      <Chip size="small" color="success" variant="outlined" label="Active" />
                    ) : (
                      <Chip size="small" color="default" variant="outlined" label="Inactive" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <StaffDialog open={adding} onClose={() => setAdding(false)} />
      <StaffDialog open={Boolean(editing)} staff={editing} onClose={() => setEditing(null)} />
    </>
  )
}
